<template>
  <div class="bg-white shadow sm:rounded-lg">
    <div class="px-4 py-5 sm:p-6">
      <h3 class="text-lg font-medium leading-6 text-gray-900">WordPress Integration</h3>
      <div class="mt-2 max-w-xl text-sm text-gray-500">
        <p>Connect your WordPress site to automatically or manually publish articles.</p>
      </div>

      <form @submit.prevent="saveSettings" class="mt-5 space-y-4">
        <div>
          <label for="site_url" class="block text-sm font-medium text-gray-700">WordPress Site URL</label>
          <div class="mt-1">
            <input
              type="url"
              name="site_url"
              id="site_url"
              v-model="settings.site_url"
              class="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="https://your-wordpress-site.com"
              required
            />
          </div>
        </div>

        <div>
          <label for="username" class="block text-sm font-medium text-gray-700">Username</label>
          <div class="mt-1">
            <input
              type="text"
              name="username"
              id="username"
              v-model="settings.username"
              class="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              required
            />
          </div>
        </div>

        <div>
          <label for="password" class="block text-sm font-medium text-gray-700">Application Password</label>
          <div class="mt-1">
            <input
              type="password"
              name="password"
              id="password"
              v-model="settings.password"
              class="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              required
            />
            <p class="mt-1 text-xs text-gray-500">
              Generate an application password in your WordPress dashboard under Users → Profile → Application Passwords
            </p>
          </div>
        </div>

        <div class="flex items-center">
          <input
            id="auto_publish"
            name="auto_publish"
            type="checkbox"
            v-model="settings.auto_publish"
            class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label for="auto_publish" class="ml-2 block text-sm text-gray-900">
            Automatically publish articles to WordPress
          </label>
        </div>

        <div class="flex items-center space-x-4">
          <button
            type="submit"
            :disabled="saving"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {{ saving ? 'Saving...' : 'Save Settings' }}
          </button>

          <button
            type="button"
            :disabled="testing"
            @click="testConnection"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {{ testing ? 'Testing...' : 'Test Connection' }}
          </button>
        </div>

        <div v-if="message" :class="['mt-2 text-sm', messageClass]">
          {{ message }}
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { WordPressService, getWordPressSettings, saveWordPressSettings } from '../services/wordpressService'

const settings = ref({
  site_url: '',
  username: '',
  password: '',
  auto_publish: false
})

const saving = ref(false)
const testing = ref(false)
const message = ref('')
const messageClass = ref('')

onMounted(async () => {
  const savedSettings = await getWordPressSettings()
  if (savedSettings) {
    settings.value = {
      ...settings.value,
      ...savedSettings
    }
  }
})

async function saveSettings() {
  saving.value = true
  message.value = ''
  
  try {
    await saveWordPressSettings(settings.value)
    message.value = 'Settings saved successfully!'
    messageClass.value = 'text-green-600'
  } catch (error) {
    message.value = 'Error saving settings: ' + error.message
    messageClass.value = 'text-red-600'
  } finally {
    saving.value = false
  }
}

async function testConnection() {
  testing.value = true
  message.value = ''
  
  try {
    const wordpress = new WordPressService(
      settings.value.site_url,
      settings.value.username,
      settings.value.password
    )
    
    const success = await wordpress.testConnection()
    
    if (success) {
      message.value = 'Connection successful!'
      messageClass.value = 'text-green-600'
    } else {
      message.value = 'Connection failed. Please check your settings.'
      messageClass.value = 'text-red-600'
    }
  } catch (error) {
    message.value = 'Error testing connection: ' + error.message
    messageClass.value = 'text-red-600'
  } finally {
    testing.value = false
  }
}
</script> 